<script setup>
  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";

  defineOptions({
    name: "ComponentPatientDoesNotMatch",
  });

  defineProps({
    currentPatient: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
  });

  const dateToHuman = (value) => {
    let parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(value)
      : /^\d{2}\.\d{2}\.\d{4}$/.test(value)
        ? new Date(value.split(".").toReversed().join("-"))
        : null;

    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString("de-DE", {dateStyle: "medium"});
  };

  const genderToHuman = (value) => {
    switch (value) {
      case "male":
        return "männlich";
      case "female":
        return "weiblich";
      case "diverse":
        return "divers";
      default:
        return null;
    }
  };
</script>

<template>
  <div>
    <ComponentAlert type="error" :test-id="getTestId('patient-does-not-match')">
      <slot>
        <p>
          Die importierten Daten passen nicht zum aktuell geöffneten Patienten.
          <br />
          Möchten Sie einen neuen Fall unter Beibehaltung der Stammdaten des aktuellen Patienten importieren?
        </p>
      </slot>
    </ComponentAlert>

    <div class="grid grid-cols-2 gap-8 p-4">
      <div class="max-w-sm">
        <h5 class="">
          <span class="font-semibold">Daten aus Import</span>
          <span class="font-normal">(werden verworfen)</span>
        </h5>
        <ComponentCard>
          <div v-if="currentPatient['title']">
            &nbsp;
            <!-- spacer when title exists -->
          </div>

          <div class="text-xl">
            {{ patient["firstname"] }}
            {{ patient["lastname"] }}
          </div>

          <div class="relative">
            <component-icon class="absolute right-0 bottom-0 text-xl text-mcred">warning</component-icon>

            <div class="pr-4">
              <span v-if="patient['birthdate']" class="after:content-[',_'] last:after:content-['']">
                {{ dateToHuman(patient["birthdate"]) }}
              </span>

              <span v-if="patient['gender']" class="after:content-[',_'] last:after:content-['']">
                {{ genderToHuman(patient["gender"]) }}
              </span>

              <span v-if="patient['insurancenumber']" class="after:content-[',_'] last:after:content-['']">
                {{ patient["insurancenumber"] }}
              </span>
            </div>
          </div>
        </ComponentCard>
      </div>

      <div class="max-w-sm">
        <h5 class="relative">
          <span class="font-semibold">Daten in Patientenakte</span>
          <span class="font-normal">(bleiben bestehen)</span>
        </h5>

        <ComponentCard>
          <div>
            {{ currentPatient["title"] }}
          </div>

          <div class="text-xl">
            {{ currentPatient["firstname"] }}
            {{ currentPatient["suffix"] }}
            {{ currentPatient["prefix"] }}
            {{ currentPatient["lastname"] }}
          </div>

          <div class="relative">
            <ComponentIcon class="absolute right-0 bottom-0 text-xl text-mcgreen">check_circle</ComponentIcon>

            <div class="pr-4">
              <span v-if="currentPatient['birthdate_formatted']" class="after:content-[',_'] last:after:content-['']">
                {{ currentPatient["birthdate_formatted"] }}
              </span>

              <span v-if="currentPatient['gender']" class="after:content-[',_'] last:after:content-['']">
                {{ genderToHuman(currentPatient["gender"]) }}
              </span>

              <span v-if="currentPatient['insurancenumber']" class="after:content-[',_'] last:after:content-['']">
                {{ currentPatient["insurancenumber"] }}
              </span>
            </div>
          </div>
        </ComponentCard>
      </div>
    </div>
  </div>
</template>
